import React, {useState, useEffect} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {db, firestore} from "../firebase";
import {doc, getDoc, getDocs, collection, query, where} from "firebase/firestore";
import {ref, set} from "firebase/database";
import {useAuth} from "../contexts/AuthContext";

export default function PaymentSuccess() {
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [licenseDetails, setLicenseDetails] = useState(null);
  const [error, setError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const {currentUser} = useAuth();

  const params = new URLSearchParams(location.search);
  const orderId = params.get("order_id");

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) {
        setError("주문 ID가 없습니다.");
        setLoading(false);
        return;
      }

      try {
        // 결제 정보 조회 (Firestore)
        const orderDoc = await getDoc(doc(firestore, "payments", orderId));
        if (!orderDoc.exists()) {
          setError("주문 정보를 찾을 수 없습니다.");
          setLoading(false);
          return;
        }

        const orderData = orderDoc.data();
        setOrderDetails(orderData);

        // 라이선스 정보 조회 (Firestore)
        const licensesSnapshot = await getDocs(
          query(collection(firestore, "licenses"), where("order_id", "==", orderId))
        );

        if (!licensesSnapshot.empty) {
          const licenseData = licensesSnapshot.docs[0].data();
          setLicenseDetails(licenseData);

          // 결제 완료 후 학습 시작 표시 (Realtime Database)
          if (currentUser && orderData.status === "completed") {
            await activateLearningAccess(currentUser.uid, licenseData);
          }
        }
      } catch (error) {
        console.error("데이터 조회 에러:", error);
        setError("주문 정보를 불러오는 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, currentUser]);

  // 결제 완료 후 학습 데이터 활성화 함수
  const activateLearningAccess = async (userId, licenseData) => {
    try {
      // 학습 데이터 활성화 (Realtime Database)
      const learningRef = ref(db, `learning/${userId}`);
      await set(learningRef, {
        active: true,
        activatedAt: new Date().toISOString(),
        expiresAt: licenseData.expires_at.toDate().toISOString(),
        licenseType: licenseData.product,
        licenseKey: licenseData.license_key
      });

      //console.log("학습 데이터 활성화 완료");
    } catch (error) {
      console.error("학습 데이터 활성화 실패:", error);
      setError("학습 데이터 활성화 중 오류가 발생했습니다.");
    }
  };

  const handleGoToDashboard = () => {
    navigate("/dashboard");
  };

  if (loading) {
    return <div className="container mx-auto p-4 text-center">로딩 중...</div>;
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
        <button
          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
          onClick={handleGoToDashboard}
        >
          대시보드로 이동
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
        <h1 className="text-2xl font-bold">결제 완료!</h1>
        <p>결제가 성공적으로 처리되었습니다. 학습 서비스가 활성화되었습니다.</p>
      </div>

      {orderDetails && (
        <div className="bg-white shadow-md rounded p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">주문 정보</h2>
          <p><strong>상품명:</strong> {orderDetails.item_name}</p>
          <p><strong>수량:</strong> {orderDetails.quantity}</p>
          <p><strong>결제 금액:</strong> {Number(orderDetails.total_amount).toLocaleString()}원</p>
          <p><strong>결제 상태:</strong> {orderDetails.status === "completed" ? "완료" : "처리 중"}</p>
          <p><strong>주문 번호:</strong> {orderId}</p>
        </div>
      )}

      {licenseDetails && (
        <div className="bg-white shadow-md rounded p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">라이선스 정보</h2>
          <p><strong>제품:</strong> {licenseDetails.product}</p>
          <p><strong>라이선스 키:</strong> <span className="font-mono bg-gray-100 p-1">{licenseDetails.license_key}</span></p>
          <p><strong>만료일:</strong> {new Date(licenseDetails.expires_at.toDate()).toLocaleDateString()}</p>
          <p><strong>상태:</strong> {licenseDetails.status === "active" ? "활성" : "비활성"}</p>
        </div>
      )}

      <div className="mt-6">
        <p className="mb-4">* 학습 서비스가 활성화되었습니다. 이제 프로그램을 이용하실 수 있습니다.</p>
        <button
          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
          onClick={handleGoToDashboard}
        >
          대시보드로 이동
        </button>
      </div>
    </div>
  );
}